import { inject, injectable } from 'inversify'
import { get } from 'lodash-es'
import type { PaginationUrlFilterScalarValueType } from '@/services/tables/types'
import {
  type FilterToFormattedTextConvertable,
  type FormattedTextNode,
  FilterOperationEnum,
  type FormattedTextBadgesValueItem,
} from '@/services/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type TranslateService from '@/services/translateService'
import type { FilterToCreate } from '@/services/tables/filters/types'

@injectable()
export default class OptionFilterMapper implements FilterToFormattedTextConvertable {
  constructor(@inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService) {}

  public toFormattedText(
    filterValue: PaginationUrlFilterScalarValueType,
    { callback }: FilterToCreate,
  ): FormattedTextNode[] {
    const valueToTextMap = this.getOptionValueToTextMap(callback)

    const ids = get(filterValue, [FilterOperationEnum.in], []) as Array<string | '0'>
    const hasEmptyOption = ids.includes('0')

    const result: FormattedTextNode[] = []
    if (hasEmptyOption) {
      result.push({
        type: 'italic',
        value: this.translateService.t('myContactsFilters.isEmpty'),
      })
    }

    const badges: FormattedTextBadgesValueItem[] = ids
      .filter((id) => id !== '0')
      .map((id) => {
        return {
          name: valueToTextMap[id] ?? id,
        }
      })
    if (badges.length > 0) {
      result.push({
        type: 'badges',
        value: badges,
      })
    }
    return result
  }

  protected getOptionValueToTextMap(callback: FilterToCreate['callback']): Record<string, string> {
    const data = callback?.({
      id: '',
      options: [],
    })
    // Only static options are supported
    // We can't fetch options here
    const options = data?.options && Array.isArray(data.options) ? data.options : []
    return options.reduce(
      (acc, option) => {
        acc[option.value] = option.text
        return acc
      },
      {} as Record<string, string>,
    )
  }
}
